import { Spinner } from '@acies/ui';
import type { ICalculationStatus } from '../../../domain/shared';

/**
 * The status of a position's calculation, shown as an icon.
 *
 * A Spinner rather than a plain Icon, because two of the four states are a calculation the engine has not finished, and those are a spinner. The other two come to rest as an icon does.
 */
export default class CalculationStatus extends Spinner {
	public set status(value: ICalculationStatus) {
		if (this.#status === value) {
			return;
		}
		this.#status = value;
		this.#statusChanged = true;
		this.invalidateProperties();
	}

	public get status(): ICalculationStatus {
		return this.#status;
	}

	/**
	 * Whether the position is waiting on an answer rather than on the engine: its configuration has a parameter nobody has answered, and the engine will not price it until someone does.
	 *
	 * Said on top of the status rather than as one of its values, because the status is the engine's vocabulary for a calculation — see `CalculationStatus` in the domain — and a position nobody can price has no calculation to have a status. While it is set, the icon shows the same warning as `draftInvalid` and does
	 * not turn, whatever the status: either way the position is waiting on the user to put something right, and there is nothing to wait for but them.
	 */
	public set awaitingAnswers(value: boolean) {
		if (this.#awaitingAnswers === value) {
			return;
		}
		this.#awaitingAnswers = value;
		this.#statusChanged = true;
		this.invalidateProperties();
	}

	public get awaitingAnswers(): boolean {
		return this.#awaitingAnswers;
	}

	/**
	 * Whether the row is being edited, and what was typed into it cannot be saved: Enter has been refused for a field that is not valid.
	 *
	 * A warning rather than the error a failed calculation shows, because nothing has been sent and nothing has gone wrong: the row is waiting on the user to mend a field. Said here rather than on the field, so the one place a row says what is wrong with its position says this too. It is shown over the status, and
	 * looks the same as awaitingAnswers.
	 */
	public set draftInvalid(value: boolean) {
		if (this.#draftInvalid === value) {
			return;
		}
		this.#draftInvalid = value;
		this.#statusChanged = true;
		this.invalidateProperties();
	}

	public get draftInvalid(): boolean {
		return this.#draftInvalid;
	}

	#awaitingAnswers: boolean;
	#draftInvalid: boolean;
	#status: ICalculationStatus;
	#statusChanged: boolean;

	public constructor() {
		super();
		this.#awaitingAnswers = false;
		this.#draftInvalid = false;
		// a position starts out uncalculated, so the icon starts out turning
		this.#status = 'pending';
		this.#statusChanged = false;
		this.name = 'refresh-cw-04';
		this.spin = true;
		// Spinner asks for 28, which is larger than every other icon in a position row
		this.width = 18;
		this.height = 18;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#statusChanged) {
			this.#commitStatus();
		}
	}

	/**
	 * Turning is part of what a status looks like, so it is set here with the icon.
	 *
	 * Setting spin invalidates again, which commits it on the way back out. The status has already been marked as committed by then, so it is only the turning that is looked at a second time.
	 */
	#commitStatus(): void {
		this.#statusChanged = false;
		// waiting on the user rather than on the engine, whether for a field to be mended or a question to be answered: a warning, still
		if (this.#draftInvalid || this.#awaitingAnswers) {
			this.name = 'alert-triangle';
			this.variant = 'warning';
			this.spin = false;
			return;
		}
		if (this.status === 'succeeded') {
			this.name = 'check-circle';
			this.variant = 'default';
		} else if (this.status === 'failed') {
			this.name = 'alert-circle';
			this.variant = 'error';
		} else {
			this.name = 'refresh-cw-04';
			this.variant = 'default';
		}
		// the two states that are not an answer, which is what there is to wait for
		this.spin = this.status === 'pending' || this.status === 'processing';
	}
}
customElements.define('cf-calculation-status', CalculationStatus);
